/**
 * Shopify Webview Callback Handler for Native Apps
 */

class WebviewHandler {
  constructor(options = {}) {
    this.debug = options.debug || false;
    this.initialized = false;
    this.pendingEvents = [];
    this.isMagnoliaWebView = false;
    this.emitWebview = () => {};
    // Initialize when DOM is ready
    this.initialize();
  }

  /**
   * Initializing the callback handler
   */
  initialize() {
    if (this.initialized) return;
    this.initialized = true;
    this.isIOS = !!window.webkit?.messageHandlers;
    this.isAndroid = !!window.android;
    let iosWebViewInterface = window.webkit?.messageHandlers?.redirectToNative;
    if (iosWebViewInterface) {
      iosWebViewInterface = iosWebViewInterface?.postMessage.bind(iosWebViewInterface);
    }
    const androidWebViewInterface = (payload) => window.android?.redirectToNative(payload);
    this.emitWebview = window.android ? androidWebViewInterface : iosWebViewInterface;
    // Use early-injected global or fallback to localStorage-based override
    const override =
      typeof window.__IS_MAGNOLIA_WEBVIEW__ !== 'undefined'
        ? window.__IS_MAGNOLIA_WEBVIEW__
        : this.getWebViewOverride();
    const userAgent = navigator.userAgent || '';
    const isLegitIOSWebView = this.isIOS && !userAgent.includes('Safari') && !userAgent.includes('CriOS');
    this.isMagnoliaWebView = override !== null ? override : isLegitIOSWebView || this.isAndroid;
    this.log('Initializing webview callback handler');
    this.setupObservers();
  }

  /**
   * Set up observers for Page interactions
   */
  setupObservers() {
    if (!this.isMagnoliaWebView) return;
    // Track click events on elements with data-webview-payload attribute
    window.addEventListener('click', (e) => {
      let webviewPayload = e.target.dataset.webviewPayload || e.target.closest('a')?.dataset.webviewPayload;
      if (webviewPayload) {
        e.preventDefault(); // Prevent default action & stop event
        try {
          webviewPayload = JSON.parse(webviewPayload);
          window.trackAppEvent(webviewPayload?.payload);
        } catch (err) {
          this.log('Failed to parse webview payload:', err);
        }
      }
    });
  }

  /**
   * Send data to the native app
   */
  sendToApp(webviewPayload) {
    if (this.emitWebview) {
      const payload = JSON.stringify({
        status: 200,
        message: '',
        data: webviewPayload,
      });
      this.emitWebview(payload);
      this.log('Sending payload to app:', payload);
    }
  }

  /**
   * Get webview override from query/localStorage (only in dev/local)
   * Test webview via query params and local storage:
   * ?webview=true will turn on webview mode
   * ?webview=false will turn off webview mode
   */
  getWebViewOverride() {
    if (window.isLocal || window.isDev) {
      const urlParams = new URLSearchParams(window.location.search);
      const webviewParam = urlParams.get('webview');

      if (webviewParam === 'true') {
        localStorage.setItem('forceWebView', 'true');
      } else if (webviewParam === 'false') {
        localStorage.removeItem('forceWebView');
      }
    }

    // we are restricting writing to localStorage above to local and dev,
    // but NOT restricting reading from local storage from any env, incl. prod
    const stored = localStorage.getItem('forceWebView');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
  }

  log(...args) {
    if (this.debug) {
      console.log('WebviewCallback', ...args);
    }
  }
}

// Initialize callback handler
const webviewHandler = new WebviewHandler({
  debug: true, // Set to false in production
});

// Expose global tracking function
window.trackAppEvent = function (webviewPayload) {
  if (webviewHandler) {
    webviewHandler.sendToApp(webviewPayload);
  }
};

window.isMagnoliaWebView = webviewHandler.isMagnoliaWebView;
window.isIOS = webviewHandler.isIOS;
window.isAndroid = webviewHandler.isAndroid;
